/**
 * fieldFill — pure helpers for CB-5.6 guided field completion, extracted from FieldFiller.tsx so the
 * JSONB value shapes (the seal-critical contract Epic 6 renders) and the completeness predicate are
 * unit-testable headlessly.
 */

import type { SignatureValue } from '@/components/settings/contracts/storedSignature';

/** The JSONB value written for an applied signature/initials field. Mirrors CB-3.7's stored-signature
 *  shape so the seal/flatten pipeline (Epic 6) renders ceremony- and stored-applied signatures
 *  identically: `value` is the primary representation, `kind` discriminates image vs typed. */
export function signatureFieldValue(sig: SignatureValue): Record<string, unknown> {
  const image = sig.signature_image || null;
  const typed = sig.signature_typed || null;
  return {
    value: image || typed,
    kind: image ? 'image' : 'typed',
    image,
    typed,
    style: sig.signature_style || null,
    // FR-56 (CB-5.10): how the signature was produced, read server-side at Finish for the audit.
    // Falls back to the kind discriminator if adoption didn't record it (older fills).
    method: sig.signature_method || (image ? 'drawn' : 'typed'),
    source: 'adopted',
  };
}

/** The JSONB value for an auto-filled Date-Signed field. `date` is a UTC YYYY-MM-DD string (NFR-8 —
 *  server/UTC date semantics; rendered in signer-local tz only at display). */
export function dateSignedValue(utcDate: string): Record<string, unknown> {
  return { value: utcDate };
}

/** Today's date in UTC (YYYY-MM-DD). */
export function todayUtcDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A field value is "empty" (unfilled) when its inner `value` is null/undefined/''. `false`/`0` are
 *  filled (a checkbox explicitly set to false is still a filled value). */
export function isEmptyValue(v: Record<string, unknown> | null | undefined): boolean {
  if (!v) return true;
  const inner = v.value;
  return inner === null || inner === undefined || inner === '';
}
